import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [skipRules, setSkipRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddSkip, setShowAddSkip] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingSkip, setEditingSkip] = useState(null);
  const [form, setForm] = useState({ name: '', match_field: 'subject', match_value: '', category_id: '', priority: 0, auto_close: false });
  const [skipForm, setSkipForm] = useState({ name: '', match_field: 'from', match_value: '' });
  const [saving, setSaving] = useState(false);
  const [savingSkip, setSavingSkip] = useState(false);
  const { staff } = useAuth();

  const fetchAll = async () => {
    try {
      const [rulesRes, catsRes, skipRes] = await Promise.all([
        api.get('/rules'),
        api.get('/categories'),
        api.get('/skiprules'),
      ]);
      setRules(rulesRes.data.rules || []);
      setCategories(catsRes.data.categories || []);
      setSkipRules(skipRes.data.rules || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', match_field: 'subject', match_value: '', category_id: '', priority: 0, auto_close: false });
    setShowAdd(true);
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ name: r.name, match_field: r.match_field, match_value: r.match_value, category_id: r.category_id || '', priority: r.priority || 0, auto_close: r.auto_close || false });
    setShowAdd(true);
  };

  const save = async () => {
    if (!form.name || !form.match_value) return alert('Name and match value required');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/rules/${editing}`, form);
      } else {
        await api.post('/rules', form);
      }
      setShowAdd(false);
      setEditing(null);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await api.delete(`/rules/${id}`);
    fetchAll();
  };

  const openAddSkip = () => {
    setEditingSkip(null);
    setSkipForm({ name: '', match_field: 'from', match_value: '' });
    setShowAddSkip(true);
  };

  const openEditSkip = (r) => {
    setEditingSkip(r.id);
    setSkipForm({ name: r.name, match_field: r.match_field, match_value: r.match_value });
    setShowAddSkip(true);
  };

  const saveSkip = async () => {
    if (!skipForm.name || !skipForm.match_value) return alert('Name and match value required');
    setSavingSkip(true);
    try {
      if (editingSkip) {
        await api.put(`/skiprules/${editingSkip}`, skipForm);
      } else {
        await api.post('/skiprules', skipForm);
      }
      setShowAddSkip(false);
      setEditingSkip(null);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingSkip(false);
    }
  };

  const deleteSkipRule = async (id) => {
    if (!window.confirm('Delete this skip rule?')) return;
    await api.delete(`/skiprules/${id}`);
    fetchAll();
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' };

  if (staff?.role !== 'admin') {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#c00' }}>Admin access required</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

      {/* ── Filter rules ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Filter rules</h2>
            <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>Auto-categorise incoming emails based on subject or sender</p>
          </div>
          <button onClick={openAdd}
            style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            + Add rule
          </button>
        </div>

        {showAdd && (
          <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>{editing ? 'Edit rule' : 'New rule'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}>Rule name</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Repeat prescription emails" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Match field</label>
                <select value={form.match_field} onChange={e => setForm(p => ({ ...p, match_field: e.target.value }))} style={inputStyle}>
                  <option value="subject">Subject line</option>
                  <option value="from">Sender email</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Contains text</label>
                <input value={form.match_value} onChange={e => setForm(p => ({ ...p, match_value: e.target.value }))}
                  placeholder="e.g. Repeat prescription" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Assign to category</label>
                <select value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))} style={inputStyle}>
                  <option value="">No category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Priority (higher = checked first)</label>
                <input type="number" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: parseInt(e.target.value) || 0 }))} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '20px' }}>
                <input type="checkbox" id="auto_close" checked={form.auto_close || false} onChange={e => setForm(p => ({ ...p, auto_close: e.target.checked }))} />
                <label htmlFor="auto_close" style={{ fontSize: '13px', color: '#333', cursor: 'pointer' }}>Auto-close matching tickets</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={save} disabled={saving}
                style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : editing ? 'Save changes' : 'Add rule'}
              </button>
              <button onClick={() => { setShowAdd(false); setEditing(null); }}
                style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading...</div>
          ) : rules.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>No rules yet — add your first rule!</div>
          ) : rules.map((r, i) => (
            <div key={r.id} style={{ padding: '14px 16px', borderBottom: i < rules.length - 1 ? '1px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>{r.name}</p>
                <p style={{ fontSize: '12px', color: '#888' }}>
                  If <strong>{r.match_field === 'subject' ? 'subject' : 'sender'}</strong> contains <strong>"{r.match_value}"</strong>
                  {r.category_name && (
                    <span> &rarr; <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '11px', background: r.category_color + '22', color: r.category_color }}>{r.category_name}</span></span>
                  )}
                  {r.auto_close && <span style={{ color: '#c00', marginLeft: '6px' }}>· Auto-close</span>}
                  {r.priority > 0 && <span style={{ color: '#bbb' }}> · Priority {r.priority}</span>}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => openEdit(r)}
                  style={{ padding: '5px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: '#fff' }}>
                  Edit
                </button>
                <button onClick={() => deleteRule(r.id)}
                  style={{ padding: '5px 12px', border: '1px solid #ffcccc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: '#fff', color: '#c00' }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Skip rules ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Skip rules</h2>
            <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>Emails matching these rules will be ignored and never create a ticket</p>
          </div>
          <button onClick={openAddSkip}
            style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            + Add skip rule
          </button>
        </div>

        {showAddSkip && (
          <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>{editingSkip ? 'Edit skip rule' : 'New skip rule'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}>Rule name</label>
                <input value={skipForm.name} onChange={e => setSkipForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. NHS emails" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Match field</label>
                <select value={skipForm.match_field} onChange={e => setSkipForm(p => ({ ...p, match_field: e.target.value }))} style={inputStyle}>
                  <option value="from">Sender email</option>
                  <option value="subject">Subject line</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Contains text</label>
                <input value={skipForm.match_value} onChange={e => setSkipForm(p => ({ ...p, match_value: e.target.value }))}
                  placeholder="e.g. nhs.net" style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={saveSkip} disabled={savingSkip}
                style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', opacity: savingSkip ? 0.7 : 1 }}>
                {savingSkip ? 'Saving...' : editingSkip ? 'Save changes' : 'Add skip rule'}
              </button>
              <button onClick={() => { setShowAddSkip(false); setEditingSkip(null); }}
                style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading...</div>
          ) : skipRules.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>No skip rules yet — add your first skip rule!</div>
          ) : skipRules.map((r, i) => (
            <div key={r.id} style={{ padding: '14px 16px', borderBottom: i < skipRules.length - 1 ? '1px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>{r.name}</p>
                <p style={{ fontSize: '12px', color: '#888' }}>
                  If <strong>{r.match_field === 'subject' ? 'subject' : 'sender'}</strong> contains <strong>"{r.match_value}"</strong> &rarr; <span style={{ color: '#c00' }}>skip email</span>
                </p>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => openEditSkip(r)}
                  style={{ padding: '5px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: '#fff' }}>
                  Edit
                </button>
                <button onClick={() => deleteSkipRule(r.id)}
                  style={{ padding: '5px 12px', border: '1px solid #ffcccc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: '#fff', color: '#c00' }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
